import React, { useEffect, useState } from "react";
import styles from "./CategoryContainer.module.scss";
import Select from "../../../../ui/Select/Select";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCategoryBitrix,
  updateCategoryStageBitrix,
} from "../../../../utils/api/actions/integrations/bitrix";
import Button from "../../../../ui/Button/Button";
import { StageAssistant } from "./StageAssistant/StageAssistant";
import { errorToast } from "../../../../utils/methods/toast";
import CategoriesWithAssistantsList from "../CategoriesWithAssistantsList/CategoriesWithAssistantsList";

export const CategoryContainer = ({ categories, combinedDataMessengers }) => {
  const dispatch = useDispatch();
  const { dataAssistants } = useSelector((state) => state?.assistant);

  const sortedCategories = React.useMemo(() => {
    return categories.slice().sort((a, b) => a.sort - b.sort);
  }, [categories]);

  const [selectedCategory, setSelectedCategory] = useState(
    categories[categories.length - 1] || null
  );
  const [assistantId, setAssistantId] = useState(
    selectedCategory?.assistantId || null
  );
  const [stageMessengers, setStageMessengers] = useState({});
  const [messageInstructions, setMessageInstructions] = useState({});
  const [originalStageData, setOriginalStageData] = useState({});

  const [stageAssistants, setStageAssistants] = useState({});
  const [initDialog, setInitDialog] = useState({});
  const [loading, setLoading] = useState(false);

  // Функция для генерации отображаемого имени пользователя
  const getUserDisplayName = (user) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    } else if (user.userName) {
      return user.userName;
    } else if (user.phoneNumber) {
      return user.phoneNumber;
    } else {
      return user.userId.substring(0, 8);
    }
  };

  // Функция для получения уникального идентификатора
  const getUserUniqueId = (user) => {
    return user.internalTelegramId || user.internalWhatsappId || user.userId;
  };

  // Функция для определения типа аккаунта
  const getAccountType = (user) => {
    if (user.internalTelegramId) return "Telegram";
    if (user.internalWhatsappId) return "WhatsApp";
    return "Неизвестно";
  };

  // Функция для получения деталей пользователя
  const getUserDetails = (user) => {
    return `${user.phoneNumber} • ${getAccountType(user)}`;
  };

  // Функция для получения значений для поиска
  const getUserSearchValues = (user) => [
    getUserDisplayName(user),
    user.phoneNumber || "",
    user.userName || "",
  ];

  // Обработчик изменения выбранных пользователей для конкретного этапа
  const handleChange = (stageId) => (selected) => {
    setStageMessengers((prev) => ({
      ...prev,
      [stageId]: selected,
    }));

    // Отмечаем, что были внесены изменения
    setStageAssistants((prev) => ({
      ...prev,
      [stageId]: {
        ...prev[stageId],
        editMode: true,
      },
    }));
  };

  // Функция для поиска пользователей мессенджеров по id
  const findMessengerUserById = React.useCallback(
    (userId, clientIdentifier, messengerType) => {
      return combinedDataMessengers.find((user) => {
        if (messengerType === "telegram" && user.internalTelegramId) {
          return (
            user.userId === userId || user.phoneNumber === clientIdentifier
          );
        } else if (messengerType === "whatsapp" && user.internalWhatsappId) {
          return (
            user.userId === userId || user.phoneNumber === clientIdentifier
          );
        }
        return false;
      });
    },
    [combinedDataMessengers]
  );

  // Инициализация при первом рендере
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[categories.length - 1] || null);
    }
  }, [categories, selectedCategory]);

  // Обновляем состояние при выборе категории
  useEffect(() => {
    if (!selectedCategory) return;

    setAssistantId(selectedCategory.assistantId);

    if (selectedCategory.stages) {
      const stageAssistantsObj = {};
      const initDialogState = {};
      const messengersState = [];
      const instructionsState = {};

      selectedCategory.stages.forEach((stage) => {
        const hasValidAssistant =
          stage.assistantId &&
          stage.assistantId !== "00000000-0000-0000-0000-000000000000";

        stageAssistantsObj[stage.id] = {
          assistantId: hasValidAssistant ? stage.assistantId : null,
          hasAssistant: hasValidAssistant,
          editMode: false,
        };

        initDialogState[stage.id] = stage.initDialog || false;

        if (stage.messengers && stage.messengers.length > 0) {
          // Находим соответствующих пользователей в списке мессенджеров
          const selectedMessengerUsers = stage.messengers
            .map((messenger) =>
              findMessengerUserById(
                messenger.userId,
                messenger.clientIdentifier,
                messenger.messengerType
              )
            )
            .filter(Boolean); // Убираем undefined значения

          messengersState[stage.id] = selectedMessengerUsers;
        } else {
          messengersState[stage.id] = [];
        }

        // Сохраняем инструкции для сообщений
        instructionsState[stage.id] = stage.initPrompt || "";
      });

      setStageAssistants(stageAssistantsObj);
      setOriginalStageData(stageAssistantsObj);
      setInitDialog(initDialogState);
      setStageMessengers(messengersState);
      setMessageInstructions(instructionsState);
    }
  }, [selectedCategory, findMessengerUserById]);

  useEffect(() => {
    if (
      assistantId &&
      assistantId !== "00000000-0000-0000-0000-000000000000" &&
      selectedCategory?.id
    ) {
      let data = {
        categoryId: selectedCategory.id,
        assistantId,
      };
      dispatch(updateCategoryBitrix(data));
    }
  }, [assistantId, dispatch, selectedCategory?.id]);

  const getAssistantName = React.useCallback(
    (assistantId) => {
      if (
        !assistantId ||
        assistantId === "00000000-0000-0000-0000-000000000000"
      ) {
        return "Ассистент не выбран";
      }

      const assistant = dataAssistants?.find(
        (assistant) => assistant.id === assistantId
      );
      return assistant?.name || "Ассистент не выбран";
    },
    [dataAssistants]
  );

  const assistantName = getAssistantName(selectedCategory?.assistantId);
  const categoryName = selectedCategory?.name || "Категория не выбрана";

  const selectObserver = (val) => {
    const selected = categories.find((category) => category.id === val);
    setSelectedCategory(selected);
  };

  const selectObserverAssistant = (val) => {
    setAssistantId(val);
  };

  const selectObserverAssistantForStage = (stageId, val) => {
    if (!stageAssistants[stageId]?.editMode) {
      setOriginalStageData((prev) => ({
        ...prev,
        [stageId]: { ...(stageAssistants[stageId] || {}) },
      }));
    }

    setStageAssistants((prev) => ({
      ...prev,
      [stageId]: { ...(prev[stageId] || {}), assistantId: val, editMode: true },
    }));
  };

  const addAssistantToStage = (stageId) => {
    setOriginalStageData((prev) => ({
      ...prev,
      [stageId]: { assistantId: null, hasAssistant: false, editMode: false },
    }));

    setStageAssistants((prev) => ({
      ...prev,
      [stageId]: { assistantId: null, hasAssistant: true, editMode: true },
    }));
  };

  const removeAssistantFromStage = (stageId) => {
    setLoading(true);

    let data = {
      categoryStageId: stageId,
      assistantId: "00000000-0000-0000-0000-000000000000",
      initDialog: false,
      messengers: [],
    };

    dispatch(updateCategoryStageBitrix(data))
      .then(() => {
        // Успешное удаление
        setStageAssistants((prev) => ({
          ...prev,
          [stageId]: {
            assistantId: null,
            hasAssistant: false,
            editMode: false,
          },
        }));
        setInitDialog((prev) => ({
          ...prev,
          [stageId]: false,
        }));
        setStageMessengers((prev) => ({
          ...prev,
          [stageId]: [],
        }));
        setMessageInstructions((prev) => ({
          ...prev,
          [stageId]: "",
        }));
      })
      .catch((err) => {
        errorToast("Произошла ошибка!");
      })
      .finally(() => {
        setLoading(false); // Отключаем индикатор загрузки
      });
  };

  const cancelChanges = (stageId) => {
    if (originalStageData[stageId]) {
      setStageAssistants((prev) => ({
        ...prev,
        [stageId]: { ...originalStageData[stageId], editMode: false },
      }));

      if (!originalStageData[stageId].hasAssistant) {
        setStageAssistants((prev) => ({
          ...prev,
          [stageId]: {
            assistantId: null,
            hasAssistant: false,
            editMode: false,
          },
        }));
      }

      // Возвращаем оригинальные настройки initDialog
      const stage = selectedCategory?.stages?.find((s) => s.id === stageId);
      if (stage) {
        setInitDialog((prev) => ({
          ...prev,
          [stageId]: stage.initDialog || false,
        }));

        // Восстанавливаем оригинальных выбранных пользователей
        if (stage.messengers && stage.messengers.length > 0) {
          const originalMessengerUsers = stage.messengers
            .map((messenger) =>
              findMessengerUserById(
                messenger.userId,
                messenger.clientIdentifier,
                messenger.messengerType
              )
            )
            .filter(Boolean);

          setStageMessengers((prev) => ({
            ...prev,
            [stageId]: originalMessengerUsers,
          }));
        } else {
          setStageMessengers((prev) => ({
            ...prev,
            [stageId]: [],
          }));
        }

        // Восстанавливаем инструкции
        setMessageInstructions((prev) => ({
          ...prev,
          [stageId]: stage.initPrompt || "",
        }));
      }
    }
  };

  const handleInitDialogChange = (stageId) => (e) => {
    setInitDialog((prev) => ({
      ...prev,
      [stageId]: e.target.checked,
    }));

    // Если отключаем опцию, очищаем выбранных пользователей
    if (!e.target.checked) {
      setStageMessengers((prev) => ({
        ...prev,
        [stageId]: [],
      }));
    }

    // Отмечаем, что были внесены изменения
    setStageAssistants((prev) => ({
      ...prev,
      [stageId]: { ...(prev[stageId] || {}), editMode: true },
    }));
  };

  const handleMessageInstructionChange = (stageId) => (e) => {
    setMessageInstructions((prev) => ({
      ...prev,
      [stageId]: e.target.value,
    }));

    // Отмечаем, что были внесены изменения
    setStageAssistants((prev) => ({
      ...prev,
      [stageId]: { ...(prev[stageId] || {}), editMode: true },
    }));
  };

  const handleSendDataEditAssistant = async (stage, assistantId) => {
    setLoading(true); // Устанавливаем индикатор загрузки в true

    // Map selected users to the messenger format required by the API
    const stageId = stage.id;
    const mappedMessengers = (stageMessengers[stageId] || []).map((user) => {
      const messengerType = user.internalTelegramId
        ? "telegram"
        : user.internalWhatsappId
        ? "whatsapp"
        : "";

      const existingMessenger = stage.messengers?.find((m) => {
        return (
          m.userId === user.userId &&
          m.messengerType === messengerType &&
          m.clientIdentifier ===
            (user.phoneNumber || user.internalWhatsappId || "")
        );
      });

      return {
        id: existingMessenger?.id || "", // Use existing ID if available
        internalCategoryStageId: stage.id,
        messengerType: messengerType,
        userId: user.userId,
        clientIdentifier: user.phoneNumber || user.internalWhatsappId || "",
      };
    });

    let data = {
      categoryStageId: stage.id,
      assistantId: assistantId,
      initDialog: initDialog[stage.id] || false,
      initPrompt: initDialog[stage.id]
        ? messageInstructions[stage.id] || ""
        : "",
      messengers: initDialog[stage.id] ? mappedMessengers : [],
    };

    try {
      // Отправка данных
      await dispatch(updateCategoryStageBitrix(data));

      // Если запрос успешен, обновляем состояние
      setStageAssistants((prev) => ({
        ...prev,
        [stage.id]: {
          assistantId: assistantId,
          hasAssistant: true,
          editMode: false,
        },
      }));

      setOriginalStageData((prev) => ({
        ...prev,
        [stage.id]: {
          assistantId: assistantId,
          hasAssistant: true,
          editMode: false,
        },
      }));
    } catch (error) {
      // Если произошла ошибка, откатываем изменения
      console.error("Ошибка при обновлении данных:", error);
      setStageAssistants((prev) => ({
        ...prev,
        [stage.id]: {
          assistantId: originalStageData[stage.id]?.assistantId || null,
          hasAssistant: originalStageData[stage.id]?.hasAssistant || false,
          editMode: false,
        },
      }));

      setOriginalStageData((prev) => ({
        ...prev,
        [stage.id]: originalStageData[stage.id] || {},
      }));
    } finally {
      // Останавливаем индикатор загрузки
      setLoading(false);
    }
  };

  function getTextColor(bgColor) {
    const hex = bgColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Вычисляем perceived brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Порог можно варьировать: 128, 150 или даже 180 — по ощущениям
    return brightness > 150 ? "black" : "white";
  }

  return (
    <div className={styles.container}>
      <CategoriesWithAssistantsList
        categories={sortedCategories}
        stageAssistants={stageAssistants}
        getAssistantName={getAssistantName}
        setSelectedCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
      />
      <Select
        containerStyle={styles.containerSelect}
        labelStyle={styles.labelSelect}
        dataSelect={sortedCategories}
        selectObserver={selectObserver}
        nameSelectedOption={categoryName}
      />
      <div className={styles.dataItemContainer}>
        <div className={styles.dataItemContainerFlex}>
          <label className={styles.title}>Ассистент</label>
        </div>
        <label className={styles.data}>
          <Select
            containerStyle={styles.containerSelectAssistant}
            labelStyle={styles.labelSelectAssistant}
            dataSelect={dataAssistants}
            selectObserver={selectObserverAssistant}
            nameSelectedOption={assistantName}
          />
        </label>
      </div>

      <div className={styles.stagesWrapper}>
        {selectedCategory?.stages ? (
          <div className={styles.stages}>
            {selectedCategory.stages
              .slice()
              .sort((a, b) => a.sort - b.sort)
              .map((item) => {
                const stageAssistantName = stageAssistants[item.id]?.assistantId
                  ? getAssistantName(stageAssistants[item.id].assistantId)
                  : "Ассистент не выбран";

                const stageMessengerUsers = stageMessengers[item.id] || [];

                return (
                  <div className={styles.stagesContainer} key={item.id}>
                    <div
                      style={{ background: item.color }}
                      className={styles.item}
                      onClick={() => console.log("stage id", item.id)}
                    >
                      <div style={{ color: getTextColor(item.color) }}>
                        {item.name}
                      </div>
                    </div>
                    {!stageAssistants[item.id]?.hasAssistant ? (
                      <div
                        style={{
                          height: "70px",
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Button
                          className={styles.btn}
                          titleClassName={styles.btnTitle}
                          leftIcon={"+"}
                          title={"Добавить ассистента"}
                          onClick={() => addAssistantToStage(item.id)}
                        />
                      </div>
                    ) : (
                      <StageAssistant
                        selectObserverAssistantForStage={
                          selectObserverAssistantForStage
                        }
                        stageAssistantName={stageAssistantName}
                        initDialog={initDialog}
                        item={item}
                        dataAssistants={dataAssistants}
                        handleInitDialogChange={handleInitDialogChange}
                        messageInstructions={messageInstructions}
                        handleMessageInstructionChange={
                          handleMessageInstructionChange
                        }
                        combinedDataMessengers={combinedDataMessengers}
                        stageMessengerUsers={stageMessengerUsers}
                        handleChange={handleChange}
                        getUserDisplayName={getUserDisplayName}
                        getUserUniqueId={getUserUniqueId}
                        getUserDetails={getUserDetails}
                        getUserSearchValues={getUserSearchValues}
                        stageAssistants={stageAssistants}
                        handleSendDataEditAssistant={
                          handleSendDataEditAssistant
                        }
                        cancelChanges={cancelChanges}
                        removeAssistantFromStage={removeAssistantFromStage}
                        loading={loading}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          <div>Нет этапов воронки</div>
        )}
      </div>
    </div>
  );
};
